import { ActionIcon, Box, Button, Paper, Text } from '@mantine/core';
import { IconInfoCircle, IconMessagePlus, IconX } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { logger } from '~portal/lib';
import {
  lengthAdvisoryBody,
  lengthAdvisoryCta,
  lengthAdvisoryDismiss,
  lengthAdvisoryIcon,
  lengthAdvisoryRow,
  lengthAdvisoryText,
  lengthAdvisoryTitle,
} from './ConversationLengthNotice.css';

/**
 * @desc The conversation-length soft nudge (H2-152): this chat has grown past the context
 * threshold, so we offer a fresh one.
 *
 * Deliberately **not** an `Alert`. `TurnGroup`'s failure presentation already draws the finer
 * distinction one step down — a refused allowance renders yellow rather than red because the user
 * did nothing wrong. An advisory is a step further out again: nothing was refused, nothing failed,
 * nothing is lost, so it takes no alert semantics and no colour. Reusing `Alert` would also mean a
 * later change to how failures look silently restyles a thing that is not a failure.
 *
 * The event carries the token figures for the audit trail; they are kept out of the copy on
 * purpose. "82,000 of 80,000 tokens" invites the reading that a limit was hit, which is exactly
 * what this is not.
 */
export function ConversationLengthNotice({ onDismiss }: { onDismiss?: () => void }) {
  const navigate = useNavigate();

  const startNewChat = () => {
    navigate({ to: '/chat/$conversationId', params: { conversationId: 'new' } }).catch((err) => {
      logger.error('[ConversationLengthNotice] Navigation to new chat failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  return (
    // Docked above the composer: a slim banner rather than a card in the transcript, so it is
    // chrome the reader can act on or put down without it costing the answer any room.
    <Paper withBorder radius="md" px="sm" py={6} bg="var(--mantine-color-body)">
      <Box className={lengthAdvisoryRow}>
        <Box component="span" className={lengthAdvisoryIcon}>
          <IconInfoCircle size={16} opacity={0.6} />
        </Box>
        {/* Title over body in one cell — see `lengthAdvisoryText`: as two columns the body was
            sized by its own longest word at tablet widths and wrapped one word to a line. */}
        <Box className={lengthAdvisoryText}>
          <Text size="sm" fw={500} className={lengthAdvisoryTitle}>
            This conversation is getting long
          </Text>
          <Text size="xs" c="dimmed" className={lengthAdvisoryBody}>
            Starting a fresh chat keeps the agent quick and focused — this one stays in your
            history.
          </Text>
        </Box>
        <Button
          variant="subtle"
          size="compact-xs"
          leftSection={<IconMessagePlus size={14} />}
          onClick={startNewChat}
          className={lengthAdvisoryCta}
        >
          Start a new chat
        </Button>
        {onDismiss && (
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Dismiss"
            onClick={onDismiss}
            className={lengthAdvisoryDismiss}
          >
            <IconX size={14} />
          </ActionIcon>
        )}
      </Box>
    </Paper>
  );
}
